import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { User as SupabaseUser } from '@supabase/supabase-js';

export interface User {
  id: string;
  auth_id: string;
  name: string;
  email: string;
  phone: string | null;
  cpf: string | null;
  address: string | null;
  is_admin: boolean;
}

interface AuthContextData {
  user: User | null;
  authUser: SupabaseUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUp: (email: string, password: string, name: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  updateUser: (data: Partial<User>) => Promise<{ success: boolean; error?: string }>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextData | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [authUser, setAuthUser] = useState<SupabaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserData = useCallback(async (authUserId: string) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('auth_id', authUserId)
        .single();

      if (error) throw error;
      return data as User;
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    if (!authUser) return;
    
    const userData = await fetchUserData(authUser.id);
    setUser(userData);
  }, [authUser, fetchUserData]);

  useEffect(() => {
    // Check initial session
    const initAuth = async () => {
      try {
        const { data: { user: currentAuthUser } } = await supabase.auth.getUser();
        
        if (currentAuthUser) {
          setAuthUser(currentAuthUser);
          const userData = await fetchUserData(currentAuthUser.id);
          setUser(userData);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        setAuthUser(session.user);
        const userData = await fetchUserData(session.user.id);
        setUser(userData);
      } else if (event === 'SIGNED_OUT') {
        setAuthUser(null);
        setUser(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchUserData]);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { success: false, error: 'Email ou senha incorretos' };
      }

      if (data.user) {
        setAuthUser(data.user);
        const userData = await fetchUserData(data.user.id);
        setUser(userData);
        return { success: true };
      }

      return { success: false, error: 'Erro ao fazer login' };
    } catch (error) {
      return { success: false, error: 'Erro ao fazer login' };
    }
  }, [fetchUserData]);

  const signUp = useCallback(async (email: string, password: string, name: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
          },
        },
      });

      if (error) {
        return { success: false, error: error.message };
      }

      if (data.user) {
        // The trigger will create the user in public.users
        // Wait a moment for the trigger to execute
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setAuthUser(data.user);
        const userData = await fetchUserData(data.user.id);
        setUser(userData);
        
        return { success: true };
      }

      return { success: false, error: 'Erro ao criar conta' };
    } catch (error) {
      return { success: false, error: 'Erro ao criar conta' };
    }
  }, [fetchUserData]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setAuthUser(null);
    setUser(null);
  }, []);

  const updateUser = useCallback(async (data: Partial<User>) => {
    if (!user) {
      return { success: false, error: 'Usuário não autenticado' };
    }

    try {
      const { error } = await supabase
        .from('users')
        .update(data)
        .eq('id', user.id);

      if (error) throw error;

      // Refresh user data
      await refreshUser();
      
      return { success: true };
    } catch (error) {
      console.error('Error updating user:', error);
      return { success: false, error: 'Erro ao atualizar dados' };
    }
  }, [user, refreshUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        authUser,
        loading,
        signIn,
        signUp,
        signOut,
        updateUser,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

